import { Select as BaseSelect } from "@base-ui/react/select";
import { Check, ChevronsUpDown } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "~/lib/cn";

export interface SelectOption<V extends string> {
  value: V;
  label: string;
}

interface SelectProps<V extends string> {
  value: V | null;
  onValueChange: (value: V) => void;
  options: readonly SelectOption<V>[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}

export const popupClass =
  "z-50 overflow-hidden rounded-[10px] border border-line bg-surface p-1 text-fg shadow-pop outline-none origin-[var(--transform-origin)] transition-[scale,opacity] duration-100 ease-out data-ending-style:scale-[0.98] data-ending-style:opacity-0 data-starting-style:scale-[0.98] data-starting-style:opacity-0";

export const itemClass =
  "flex min-h-8 cursor-default items-center gap-2 rounded-[6px] px-2.5 py-1.5 text-sm outline-none select-none data-highlighted:bg-sunken data-disabled:opacity-50 max-md:min-h-11";

function SelectInner<V extends string>(
  { value, onValueChange, options, placeholder, id, disabled, className, ...aria }: SelectProps<V>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <BaseSelect.Root
      items={options}
      value={value}
      onValueChange={(next) => {
        if (next !== null) onValueChange(next as V);
      }}
      disabled={disabled}
    >
      <BaseSelect.Trigger
        ref={ref}
        id={id}
        {...aria}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-control border border-line-strong bg-surface px-3 text-left text-sm text-fg transition-colors hover:border-fg-3 focus-visible:border-gold data-popup-open:border-gold aria-invalid:border-danger disabled:bg-surface-2 max-md:h-11 max-md:text-base",
          className,
        )}
      >
        <BaseSelect.Value className="truncate data-placeholder:text-fg-3" placeholder={placeholder} />
        <BaseSelect.Icon className="text-fg-3">
          <ChevronsUpDown className="size-4" aria-hidden />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={4} className="z-50 outline-none" alignItemWithTrigger={false}>
          <BaseSelect.Popup
            className={cn(popupClass, "max-h-[min(22rem,var(--available-height))] min-w-[var(--anchor-width)] overflow-y-auto")}
          >
            <BaseSelect.List>
              {options.map((option) => (
                <BaseSelect.Item key={option.value} value={option.value} className={itemClass}>
                  <BaseSelect.ItemText className="flex-1">{option.label}</BaseSelect.ItemText>
                  <BaseSelect.ItemIndicator>
                    <Check className="size-4 text-gold-text" aria-hidden />
                  </BaseSelect.ItemIndicator>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}

export const Select = forwardRef(SelectInner) as <V extends string>(
  props: SelectProps<V> & { ref?: React.Ref<HTMLButtonElement> },
) => React.JSX.Element;
